import { userName, userPhoto } from "./presentation";

const REPLY_FIELDS = [
    "approved_replies",
    "approvedReplies",
    "replies",
    "children",
];

export function commentId(comment) {
    const id = comment?.id;
    return id === undefined || id === null ? "" : String(id);
}

export function commentReplies(comment) {
    for (const field of REPLY_FIELDS) {
        if (Array.isArray(comment?.[field])) return comment[field];
    }
    return [];
}

function withReplies(comment, replies) {
    const field =
        REPLY_FIELDS.find((candidate) => Array.isArray(comment?.[candidate])) ||
        "approved_replies";
    return { ...comment, [field]: replies };
}

export function flattenCommentTree(
    comments,
    depth = 0,
    parentPath = "root",
    seen = new Set()
) {
    if (!Array.isArray(comments)) return [];

    return comments.reduce((rows, comment, index) => {
        if (!comment || typeof comment !== "object" || seen.has(comment)) {
            return rows;
        }

        seen.add(comment);
        const identity = comment.id ?? index;
        const path = `${parentPath}-${identity}-${index}`;
        rows.push({
            comment,
            depth,
            key: `interactive-social-wall-comment-${path}`,
        });
        rows.push(
            ...flattenCommentTree(commentReplies(comment), depth + 1, path, seen)
        );
        return rows;
    }, []);
}

export function countCommentTree(comments) {
    return flattenCommentTree(comments).length;
}

export function findCommentInTree(comments, targetId) {
    if (!Array.isArray(comments)) return null;
    const normalizedId = String(targetId);

    for (const comment of comments) {
        if (commentId(comment) === normalizedId) return comment;
        const match = findCommentInTree(commentReplies(comment), normalizedId);
        if (match) return match;
    }
    return null;
}

export function replaceCommentInTree(comments, targetId, replacement) {
    if (!Array.isArray(comments)) return { comments: [], changed: false };
    const normalizedId = String(targetId);
    let changed = false;

    const nextComments = comments.map((comment) => {
        if (changed) return comment;
        if (commentId(comment) === normalizedId) {
            changed = true;
            return replacement;
        }

        const result = replaceCommentInTree(
            commentReplies(comment),
            normalizedId,
            replacement
        );
        if (!result.changed) return comment;

        changed = true;
        return withReplies(comment, result.comments);
    });

    return { comments: changed ? nextComments : comments, changed };
}

export function addReplyToTree(comments, parentId, reply) {
    if (!Array.isArray(comments)) return { comments: [], changed: false };
    const normalizedId = String(parentId);
    let changed = false;

    const nextComments = comments.map((comment) => {
        if (changed) return comment;
        if (commentId(comment) === normalizedId) {
            changed = true;
            return withReplies(comment, [...commentReplies(comment), reply]);
        }

        const result = addReplyToTree(commentReplies(comment), normalizedId, reply);
        if (!result.changed) return comment;

        changed = true;
        return withReplies(comment, result.comments);
    });

    return { comments: changed ? nextComments : comments, changed };
}

export function removeCommentFromTree(comments, targetId) {
    if (!Array.isArray(comments)) {
        return { comments: [], removedComment: null };
    }
    const normalizedId = String(targetId);
    const directIndex = comments.findIndex(
        (comment) => commentId(comment) === normalizedId
    );

    if (directIndex >= 0) {
        return {
            comments: comments.filter((_, index) => index !== directIndex),
            removedComment: comments[directIndex],
        };
    }

    for (let index = 0; index < comments.length; index += 1) {
        const comment = comments[index];
        const result = removeCommentFromTree(commentReplies(comment), normalizedId);
        if (!result.removedComment) continue;

        return {
            comments: comments.map((item, itemIndex) =>
                itemIndex === index ? withReplies(item, result.comments) : item
            ),
            removedComment: result.removedComment,
        };
    }

    return { comments, removedComment: null };
}

export function ensureCommentUsers(comments, currentUser = {}) {
    if (!Array.isArray(comments)) return [];
    const currentUserId = currentUser?.id;
    let changed = false;

    const normalizedComments = comments.map((comment) => {
        if (!comment || typeof comment !== "object") return comment;

        const replies = commentReplies(comment);
        const normalizedReplies = ensureCommentUsers(replies, currentUser);
        let normalizedComment =
            normalizedReplies === replies
                ? comment
                : withReplies(comment, normalizedReplies);

        if (normalizedComment.user) {
            if (normalizedComment !== comment) changed = true;
            return normalizedComment;
        }

        const ownerId = normalizedComment.user_id;
        const isCurrentUser =
            ownerId !== undefined &&
            ownerId !== null &&
            currentUserId !== undefined &&
            currentUserId !== null &&
            String(ownerId) === String(currentUserId);

        normalizedComment = {
            ...normalizedComment,
            user: {
                id: ownerId ?? null,
                name: isCurrentUser ? currentUser?.name : "",
                surname: isCurrentUser ? currentUser?.surname : "",
                fullname: isCurrentUser ? userName(currentUser, "Siz") : "Kullanıcı",
                photo_link: isCurrentUser ? userPhoto(currentUser) : "",
            },
        };
        changed = true;

        return normalizedComment;
    });

    return changed ? normalizedComments : comments;
}
